import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  useDeleteBugReportTypeApi,
  useGetBugReportTypes,
} from '../../../services/redux/api/bugReportApi';
import { IBugReportType } from '../../../types/interfaces/bug-report-type';

export function useBugReportTypesComponent(classes) {
  const { t } = useTranslation();
  const { data: bugReportTypes } = useGetBugReportTypes();
  const [itemForDelete, setItemForDelete] = useState<IBugReportType>(null);
  const [itemForEditing, setItemForEditing] = useState<IBugReportType>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteBugReportType] = useDeleteBugReportTypeApi();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filteredValues = useMemo(
    () =>
      bugReportTypes?.filter((property: IBugReportType) => {
        if (!filter) return true;

        const filterValue = filter.toLowerCase();

        if (
          property.name.toLowerCase().includes(filterValue) ||
          property.nameKey.toLowerCase().includes(filterValue)
        ) {
          return true;
        }

        return false;
      }),
    [bugReportTypes, filter],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.bugReports?.canCreateUpdateType ?? false,
      canDelete: access?.bugReports?.canDeleteType ?? false,
    },
    (type: IBugReportType) => {
      setItemForEditing(type);
      setShowEditModal(true);
    },
    (type: IBugReportType) => {
      setItemForDelete(type);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.BUG_REPORT_TYPE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteBugReportType(itemForDelete.id);
  }

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
  };
}
